import axios from 'axios';
import store from '@/store';
import { attachTokenRefresh } from '@/utils/authInterceptors';

// Create axios instance for SureScript API (production only)
const sureScriptApi = axios.create({
  baseURL: 'https://www.vibrant-america.com/lisapi/v1/lis/sure-script'
});

// Add auth interceptor
sureScriptApi.interceptors.request.use(
  function(config) {
    const token = store?.state?.login?.token || '';
    if (token) {
      config.headers.Authorization = token ? `Bearer ${token}` : '';
    }
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

/**
 * Search drugs by prefix using SureScript API (production only)
 * For staging/development, returns empty array (component will use fallback data)
 * @param {string} prefix - Drug name prefix to search
 * @returns {Promise<Array<string>>} Array of drug names
 */
export const searchDrugsByPrefix = async (prefix) => {
  // Use real API for production only
  if (process.env.VUE_APP_ENV === 'production') {
    try {
      const response = await sureScriptApi.get('/epx/search_drug_by_prefix', {
        params: { prefix }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search drugs:', error);
      return [];
    }
  }

  // For staging/development, return empty array and let component use fallback data
  return [];
};

// ============ Medication Record API (separate service) ============

// Determine base URL based on environment
const medicationRecordBaseUrl = process.env.VUE_APP_ENV === 'production'
  ? 'https://api.vibrant-wellness.com/allergy-medication-record-prod'
  : 'https://api.vibrant-wellness.com/allergy-medication-record';

// Create axios instance for Medication Record API
const medicationRecordApi = axios.create({
  baseURL: medicationRecordBaseUrl
});

// Add auth interceptor for Medication Record API
medicationRecordApi.interceptors.request.use(
  function(config) {
    const token = store?.state?.login?.token || '';
    if (token) {
      config.headers.Authorization = token ? `Bearer ${token}` : '';
    }
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

attachTokenRefresh(sureScriptApi);
attachTokenRefresh(medicationRecordApi);

/**
 * Add medication record for a patient
 * POST /patient/:patient_id/add-medication-records
 * @param {string} patientId - Patient ID
 * @param {Object} medicationData - Medication data
 * @returns {Promise<Object>} API response
 */
export const addMedicationRecord = async (patientId, medicationData) => {
  const payload = {
    medication_name: medicationData.medication_name,
    dosage: medicationData.dosage || '',
    frequency_value: medicationData.frequency_value || '',
    frequency_period: medicationData.frequency_period || '',
    start_date: medicationData.start_date || '',
    end_date: medicationData.end_date || '',
    source: 'manual',
    is_active: true
  };

  try {
    const response = await medicationRecordApi.post(`/record/patient/${patientId}/add-medication-records`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to add medication record:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get medication records for a patient
 * GET /record/patient/:patient_id/medication-records
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} API response with data array and meta
 */
export const getMedicationRecords = async (patientId) => {
  try {
    const response = await medicationRecordApi.get(`/record/patient/${patientId}/medication-records`);
    return response.data;
  } catch (error) {
    console.error('Failed to get medication records:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete a medication record (soft delete)
 * DELETE /patient/:patient_id/medication-records/:medication_id
 * @param {string} patientId - Patient ID
 * @param {string} medicationId - Medication record ID
 * @returns {Promise<Object>} API response
 */
export const deleteMedicationRecord = async (patientId, medicationId) => {
  try {
    const response = await medicationRecordApi.delete(`/record/patient/${patientId}/medication-records/${medicationId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete medication record:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update a medication record
 * PUT /patient/:patient_id/medication-records/:medication_id
 * @param {string} patientId - Patient ID
 * @param {string} medicationId - Medication record ID
 * @param {Object} medicationData - Medication data to update
 * @returns {Promise<Object>} API response
 */
export const updateMedicationRecord = async (patientId, medicationId, medicationData) => {
  try {
    const response = await medicationRecordApi.put(`/record/patient/${patientId}/medication-records/${medicationId}`, medicationData);
    return response.data;
  } catch (error) {
    console.error('Failed to update medication record:', error.response?.data || error.message);
    throw error;
  }
};

// ============ Allergy Record API (same service as medication) ============

/**
 * Add allergy record for a patient
 * POST /record/patient/:patient_id/add-allergy-records
 * @param {string} patientId - Patient ID
 * @param {Object} allergyData - Allergy data
 * @returns {Promise<Object>} API response
 */
export const addAllergyRecord = async (patientId, allergyData) => {
  const payload = {
    allergen_name: allergyData.allergen_name,
    reactions: allergyData.reactions || '',
    overall_severity: allergyData.overall_severity || '',
    onset_date: new Date().toISOString().split('T')[0], // Use current date (creation time)
    source: 'manual'
  };

  try {
    const response = await medicationRecordApi.post(`/record/patient/${patientId}/add-allergy-records`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to add allergy record:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get allergy records for a patient
 * GET /record/patient/:patient_id/allergy-records
 * @param {string} patientId - Patient ID
 * @param {Object} params - Query parameters (is_active, verification_status, limit, offset)
 * @returns {Promise<Object>} API response with data array and meta
 */
export const getAllergyRecords = async (patientId, params = {}) => {
  try {
    const response = await medicationRecordApi.get(`/record/patient/${patientId}/allergy-records`, { params });
    return response.data;
  } catch (error) {
    console.error('Failed to get allergy records:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete an allergy record (soft delete)
 * DELETE /record/patient/:patient_id/allergy-records/:allergy_id
 * @param {string} patientId - Patient ID
 * @param {string} allergyId - Allergy record ID
 * @returns {Promise<Object>} API response
 */
export const deleteAllergyRecord = async (patientId, allergyId) => {
  try {
    const response = await medicationRecordApi.delete(`/record/patient/${patientId}/allergy-records/${allergyId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete allergy record:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update an allergy record
 * PUT /record/patient/:patient_id/allergy-records/:allergy_id
 * @param {string} patientId - Patient ID
 * @param {string} allergyId - Allergy record ID
 * @param {Object} allergyData - Allergy data to update
 * @returns {Promise<Object>} API response
 */
export const updateAllergyRecord = async (patientId, allergyId, allergyData) => {
  try {
    const response = await medicationRecordApi.put(`/record/patient/${patientId}/allergy-records/${allergyId}`, allergyData);
    return response.data;
  } catch (error) {
    console.error('Failed to update allergy record:', error.response?.data || error.message);
    throw error;
  }
};
